import React, { useContext, useState } from "react";
import { CartContext } from "./cartContext";
import { WishlistContext } from "./Wishlistcontext";
import { Link, useNavigate } from "react-router-dom";
import { CurrencyContext } from "./CurrencyContext";
import {
  User,
  MapPin,
  CreditCard,
  ArrowLeft,
  ChevronLeft,
  Check as CheckIcon,
  Shield,
} from "lucide-react";

const ProductCard = ({ product }) => {
  const { currencySign, convertPrice } = useContext(CurrencyContext);

  const { addToCart, cartItems } = useContext(CartContext);
  const { addToWishlist, removeFromWishlist, wishlistItems } =
    useContext(WishlistContext);
  const navigate = useNavigate();

  const [wishlistClicked, setWishlistClicked] = useState(false);

  const isInCart = cartItems.some(
    (item) =>
      `${item.category}-${item.id}-${item.name}` ===
      `${product.category}-${product.id}-${product.name}`
  );

  const isWishlisted = wishlistItems.some(
    (item) => item.id === product.id && item.category === product.category
  );

  // Toggle wishlist
  const toggleWishlist = () => {
    if (isWishlisted) removeFromWishlist(product);
    else addToWishlist(product);

    setWishlistClicked(true);
    setTimeout(() => setWishlistClicked(false), 500);
  };

  const handleAddToCart = () => {
    if (!isInCart) addToCart(product);
  };

  const isOnSale = product.sale_price && product.sale_price > 0;
  const getSmallName = (name) =>
    name.length < 25 ? name : name.slice(0, 25) + "...";

  return (
    <>
      
      <article className="bg-white shadow-lg rounded-xl overflow-hidden w-72 relative hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-1 hover:scale-105">
        {/* Sale Badge */}
        {isOnSale && (
          <span className="absolute top-3 left-3 bg-[#fe7590] text-white text-xs font-bold px-3 py-1 rounded-full z-10 uppercase shadow-md">
            Sale
          </span>
        )}

        {/* Product Image with Wishlist Icon */}
        <Link
          to={`/productinfo/${product.id}/${product.category}`}
          className="cursor-pointer relative"
        >
          <div className="relative overflow-hidden h-80 flex justify-center items-center bg-gray-50">
            <img
              src={product.image}
              alt={product.name}
              className="object-contain transition-transform duration-500 hover:scale-110"
            />

            {/* Wishlist Icon Overlay */}
            <div
              className={`absolute top-2 right-2 w-9 h-9 flex items-center justify-center rounded-full shadow-xl cursor-pointer transition-transform duration-300 ${
                wishlistClicked ? "scale-125 animate-ping" : "scale-100"
              } ${
                isWishlisted
                  ? "bg-red-100 text-white"
                  : "bg-white text-gray-500 hover:bg-red-100"
              }`}
              onClick={(e) => {
                e.preventDefault(); // prevent navigation
                toggleWishlist();
              }}
              title={isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
            >
              <img
                src={
                  isWishlisted
                    ? "/assests/icons/wa.png"
                    : "/assests/icons/w.png"
                }
                alt="Wishlist"
                className="w-5 h-5"
              />
            </div>
          </div>
        </Link>

        {/* Product Info */}
        <div className="p-4 flex flex-col gap-2">
          <Link
            to={`/productinfo/${product.id}/${product.category}`}
            className="cursor-pointer"
          >
            <h3 className="font-semibold text-black hover:text-pink-600 transition">
              {getSmallName(product.name)}
            </h3>
            <p className="text-gray-500 text-sm line-clamp-2">
              {product.description}
            </p>

            <div className="flex items-center gap-2 mt-2">
              {isOnSale ? (
                <>
                  <span className="text-[#fe7590] font-bold text-lg">
                    {currencySign}
                    {convertPrice(product.sale_price)}
                  </span>
                  <span className="text-gray-400 line-through text-sm">
                    {currencySign}
                    {convertPrice(product.price)}
                  </span>
                </>
              ) : (
                <span className="text-gray-800 font-bold text-lg">
                  {currencySign}
                  {convertPrice(product.price)}
                </span>
              )}
            </div>
          </Link>

          {/* Add to Cart Button */}
          <div className="relative">
            <button
              onClick={handleAddToCart}
              className={`flex items-center justify-center gap-2 py-2 mt-3 w-full rounded-lg border border-black transition-all duration-500 cursor-pointer ${
                isInCart
                  ? "bg-green-500 text-white"
                  : "bg-white hover:bg-red-100 hover:text-black"
              }`}
            >
              <img
                src="/assests/icons/addtocart.png"
                alt="Add to Cart"
                className={`w-6 h-6 transition-transform duration-500 ${
                  isInCart ? "rotate-[360deg] scale-125" : ""
                }`}
              />
              <span className="font-semibold text-sm">
                {isInCart ? "In Cart" : "Add to Cart"}
              </span>
            </button>
          </div>
        </div>
      </article>
    </>
  );
};

// Product Grid Component
export const ProductGrid = ({ products }) => {
  return (
    <div className="py-10 bg-gray-100 flex justify-center gap-8 flex-wrap">
      {products.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
};

export default ProductCard;
